import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit'
import { requestPartnerData } from 'legacy/lut/features/partner360/configuration'
import { mapJSONToCompleteness } from 'legacy/lut/services/partner360/completeness/mappings/mapJSONToCompleteness'

import { RequestStatus } from 'clickNgo/configs/service/common'
import { requestFinancialCockpitData } from 'partner360/features/financialCockpit/model/thunks'

import { requestRecalculateCompleteness } from './api/requestRecalculateCompleteness'
import { initialState } from './configs'
import { PARTNER360_COMPLETENESS_NAMESPACE } from './constants'
import { ICompletenessData } from './models/completeness'
import { ICompletenessState } from './models/state'

// Initialize lazily too: development hot reload can preserve state created before this field.
const getPendingSnapshots = (state: Draft<ICompletenessState>) =>
  (state.pendingSnapshotRequests ??= {})

const invalidatePendingSnapshots = (
  state: Draft<ICompletenessState>,
  context?: { partnerId: string; fYear: number }
) => {
  // A cockpit/partner snapshot requested before this result may still contain the old warning.
  Object.values(getPendingSnapshots(state)).forEach((request) => {
    if (!context || (request.partnerId === context.partnerId && request.fYear === context.fYear)) {
      request.isStale = true
    }
  })
}

const consumeStaleSnapshot = (state: Draft<ICompletenessState>, requestId: string) => {
  const pending = getPendingSnapshots(state)
  const isStale = pending[requestId]?.isStale === true
  delete pending[requestId]
  return isStale
}

const completenessSlice = createSlice({
  name: PARTNER360_COMPLETENESS_NAMESPACE,
  initialState,
  reducers: {
    loadCompleteness: (state, action: PayloadAction<ICompletenessData[]>) => {
      invalidatePendingSnapshots(state)
      state.data = action.payload
      state.status = RequestStatus.FULFILLED
      state.message = 'completeness is loaded'
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(requestPartnerData.pending, (state, action) => {
        const { partnerId, fYear } = action.meta.arg
        getPendingSnapshots(state)[action.meta.requestId] = { partnerId, fYear, isStale: false }
        state.status = RequestStatus.PENDING
        state.message = 'fetching data...'
      })
      .addCase(requestPartnerData.rejected, (state, action) => {
        if (consumeStaleSnapshot(state, action.meta.requestId)) return
        state.status = RequestStatus.REJECTED
        if (action.error.message) {
          state.message = action.error.message
        }
      })
      .addCase(requestPartnerData.fulfilled, (state, action) => {
        if (consumeStaleSnapshot(state, action.meta.requestId)) return
        state.status = RequestStatus.FULFILLED
        state.message = 'Success'
        state.data = mapJSONToCompleteness(action.payload.Completeness)
      })

      // Partner cockpit only (requested with a partnerId, as `cockpitMode: 'partner'`):
      // its one partner row carries the partner's Completeness JSON
      .addCase(requestFinancialCockpitData.pending, (state, action) => {
        const { partnerId, fYear } = action.meta.arg
        if (partnerId) {
          getPendingSnapshots(state)[action.meta.requestId] = { partnerId, fYear, isStale: false }
        }
      })
      .addCase(requestFinancialCockpitData.rejected, (state, action) => {
        consumeStaleSnapshot(state, action.meta.requestId)
      })
      .addCase(requestFinancialCockpitData.fulfilled, (state, action) => {
        if (consumeStaleSnapshot(state, action.meta.requestId)) return
        const partnerData = action.payload.PartnerData[0]

        if (action.meta.arg.partnerId && partnerData) {
          state.status = RequestStatus.FULFILLED
          state.message = 'Success'
          state.data = mapJSONToCompleteness(partnerData.Completeness)
        }
      })

      .addCase(requestRecalculateCompleteness.pending, (state) => {
        state.status = RequestStatus.PENDING
      })
      .addCase(requestRecalculateCompleteness.rejected, (state, action) => {
        state.status = RequestStatus.REJECTED
        if (action.error.message) {
          state.message = action.error.message
        }
      })
      .addCase(requestRecalculateCompleteness.fulfilled, (state, action) => {
        invalidatePendingSnapshots(state, action.meta.arg)
        state.status = RequestStatus.FULFILLED
        state.data = action.payload
      }),
})

export const { loadCompleteness } = completenessSlice.actions

export default completenessSlice.reducer
